import React, {useEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Stack} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Icon,
  Label,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';

function RephraseVideoDisplay({
  videoUrl,
  videoName,
  subtitleTime = 12,
}: {
  videoUrl: string;
  videoName: string;
  subtitleTime: number;
}) {
  const {uiInteraction} = useUiInteractionEvent();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const INITIAL_VIDEO_TIMESTAMP = '00:00/00:00';
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
      uiInteraction({
        item_name: 'rephrase_video_play',
        interaction_location: CONTENT_STUDIO_VIEW_NAMES.REPHRASE_VIDEO_DISPLAY,
        interaction_details: isPlaying ? 'pause' : 'play',
        flow_name: FLOW_NAMES.CONTENT_STUDIO.REPHRASE,
      });
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (videoRef.current) {
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const clickPosition = event.clientX - rect.left;
      const percentage = (clickPosition / rect.width) * 100;
      const newTime = (percentage / 100) * videoRef.current.duration;
      changeTime(newTime);
      uiInteraction({
        item_name: 'rephrase_video_display_progress',
        interaction_location: CONTENT_STUDIO_VIEW_NAMES.REPHRASE_VIDEO_DISPLAY,
        interaction_details: `${percentage}%`,
        flow_name: FLOW_NAMES.CONTENT_STUDIO.REPHRASE,
      });
    }
  };

  const changeTime = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  useEffect(() => {
    changeTime(subtitleTime);
  }, [subtitleTime]);

  return (
    <Stack
      paddingX="36px"
      paddingY="32px"
      position="relative"
      alignItems="center"
      justifyContent="center"
      flex={1}
      bgcolor={designSystemToken('semantic.bg.tertiary')}
    >
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            right: 'calc((100vw - 124px)*0.17)',
          }}
        >
          <CircularProgress sx={{width: '100px', height: '100px'}} />
        </Box>
      )}
      {videoUrl && (
        <Box position="relative">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            ref={videoRef}
            style={{maxWidth: '100%', maxHeight: '90vh', borderRadius: '8px'}}
            onTimeUpdate={handleTimeUpdate}
            onLoadedData={() => setIsLoading(false)}
          >
            <source src={videoUrl} />
          </video>

          <Box position="absolute" top="16px" left="12px">
            <Label size="xl" color={designSystemToken('semantic.fg.inverse')}>
              {videoName}
            </Label>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              position: 'absolute',
              bottom: '12px',
              left: '0',
              right: '12px',
            }}
          >
            <Button
              appearance="overlay"
              mode="plain"
              size="large"
              onClick={togglePlay}
              icon={
                <Icon
                  name={isPlaying ? 'Actions-Pause' : 'Actions-Play'}
                  size="large"
                  appearance="white"
                />
              }
            />
            <Box
              onClick={handleProgressClick}
              sx={{
                flex: 1,
                height: '4px',
                borderRadius: '999px',
                backgroundColor: designSystemToken(
                  'semantic.fg.inverse-secondary'
                ),
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: `${
                    (currentTime / (videoRef.current?.duration ?? 0)) * 100
                  }%`,
                  height: '100%',
                  backgroundColor: 'white',
                }}
              />
            </Box>
            <Body
              size="md"
              color={designSystemToken('semantic.fg.inverse')}
              sx={{marginLeft: '12px'}}
            >
              {videoRef.current?.duration
                ? `${formatTime(currentTime)}/${formatTime(
                    videoRef.current?.duration
                  )}`
                : INITIAL_VIDEO_TIMESTAMP}
            </Body>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

export default RephraseVideoDisplay;
